import React from 'react';
import {Card, Result, Tag, Table} from "antd";
import {flattenJson, decodeEntities} from "../utils/util";
import {isEmpty, escapeRegExp} from "lodash";
import {getMissingValues} from "./dataQA/mappingQA.util";
import {getUnmappedColumns} from "./validationMessages.util";


const ValidationMessages = (props) => {
    const isObject = (obj) => {
        var type = typeof obj;
        return type === 'function' || type === 'object' && !!obj;
    };

    const getTagColor = (issue) => {
        let color = 'green';
        switch (issue.severity) {
            case 'fatal':
                color = 'red';
                break;
            case 'error':
                color = 'magenta'
                break;
            case 'warning':
                color = 'orange'
                break;
            case 'info':
                color = 'lime'
                break;
            default:
        }
        return color;
    }

    const getNullPaths = () => {
        let missing = [];
        if (!isEmpty(props.fhirPreview)) {
            let flattened = flattenJson(props.fhirPreview)
            for (const [key, value] of Object.entries(flattened)) {
                if (value === null || value.toString().toLowerCase() === "null") {
                    if (!missing.includes(key)) {
                        missing.push(key)
                    }
                }
            }
        }
        return missing;
    }

    const getValueSetErrors = (mappings, fhirPreview) => {
        if (isEmpty(fhirPreview)) return null;
        const valueSetErrs = mappings.filter(({ path, value }) => value !== null && path.match(/coding\[\d*\]\.system/g) && value.toLowerCase().includes("valueset"));
        return valueSetErrs.length > 0 ? valueSetErrs : null;
    }

    const renderMissingValuesTable = (missingValues = []) => {
        const columns = [
            {
              title: 'Key',
              dataIndex: 'sampleDataKey',
              key: 'sampleDataKey',
              defaultSortOrder: "ascend",
                sorter: (a, b) => {
                    if (!!a.sampleDataKey && !!b.sampleDataKey) {
                        return a.sampleDataKey.localeCompare(b.sampleDataKey)
                    }
                }
            },
            {
              title: 'Value',
              dataIndex: 'sampleDataValue',
              key: 'sampleDataValue',
            },
            {
              title: 'Expected',
              dataIndex: 'exptectedFHIRValue',
              key: 'exptectedFHIRValue',
            },
            {
              title: 'Path',
              dataIndex: 'path',
              key: 'path',
              sorter: (a, b) => {
                if (!!a.path && !!b.path) {
                    return a.path.localeCompare(b.path)
                }
            }
            }
        ]

        const data = missingValues.map(({sampleKey, expectedVal, path, sampleVal}) => {
            return {
                sampleDataKey: sampleKey,
                sampleDataValue: sampleVal,
                exptectedFHIRValue: expectedVal,
                path,
            }
        })
        return <Table columns={columns} dataSource={data} pagination={false} size="small" className="missingFHIRTable"/>;
    }

    let hasUnmappedCols = null;
    let unmappedCols = null;
    let nullPaths = null;
    let missingValues = [];
    let valueSetErrors = null
    if (props.mappings) {
        if (props.sampleData) {
            unmappedCols = getUnmappedColumns(props.sampleData, props.mappings);
            hasUnmappedCols = Object.keys(unmappedCols).length !== 0;
        }
        nullPaths = getNullPaths();
        missingValues = getMissingValues(props.sampleData, props.mappings, props.fhirPreview, props.groovyOutputs);
        valueSetErrors = getValueSetErrors(props.mappings, props.fhirPreview)
    } else {
        hasUnmappedCols = true;
        nullPaths = [];
        unmappedCols = true;
    }
    
    const { id: errID, resourceType } = props.fhirErrors;
    const hasMissingValues = missingValues.length > 0
    let isError = (errID === undefined || errID === "Error" || (props.fhirPreview && props.fhirPreview.id == null)) && resourceType === 'OperationOutcome';
    let isSuccess = !isError && (errID === "NoError") && (resourceType === 'OperationOutcome');

    return (
        <>
            {
                isSuccess &&
                <Result
                    status="success"
                    title="Success!"
                    subTitle="Mapping validated to FHIR structure definition.
                    Note: There may still be issues if fields present in source data
                    are omitted from output FHIR resource."
                />
            }
            {
                isError &&
                <Result
                    status="error"
                    title="Error"
                    subTitle="Mapping is not conformant to the FHIR resource structure definition and/or profile."
                />
            }
            {
                (hasUnmappedCols && props.mappings) &&
                <>
                    <Card>
                        <Tag color="red">Missing data</Tag>
                        <p>
                            The following columns from the sample data are unmapped:
                        </p>
                        {unmappedCols.map(key => {
                            return <code style={{marginBottom: 10}} key={Math.random()}>{key}<br/></code>
                        })}
                    </Card>
                    <br/>
                </>
            }
            {
                !isEmpty(nullPaths) &&
                <>
                    <Card>
                        <Tag color="orange">Warning</Tag>
                        <Tag color="orange">null values detected</Tag>
                        <p>
                            The following paths in the FHIR output have null values
                        </p>
                        {nullPaths.map(key => {
                            return <code style={{marginBottom: 10}} key={Math.random()}>{key}<br/></code>
                        })}
                    </Card>
                    <br/>
                </>
            }
            {
                hasMissingValues &&
                <>
                    <Card style={{overflow: "scroll"}}>
                        <Tag color="orange">Warning</Tag>
                        <Tag color="orange">missing values detected</Tag>
                        <p>
                            The following values from the sample data are missing in the FHIR preview
                        </p>
                        {renderMissingValuesTable(missingValues)}
                    </Card>
                    <br/>
                </>
            }
            {
                (!hasUnmappedCols && props.sampleData) &&
                <>
                    <Card>
                        <Tag color="green">Success</Tag>
                        <p>
                            All data present in sample data has been mapped to FHIR resource(s)
                        </p>
                    </Card>
                    <br/>
                </>
            }
            {
                (valueSetErrors) &&
                <>
                    <Card>
                        <Tag color="red">Value Set</Tag>
                        <p>
                            Value sets are not viable system values, please update the following paths to a contained code set system value instead. 
                        </p>
                        {valueSetErrors.map(({path}) => <code key={path}>{path}<br/></code>)}
                    </Card>
                    <br/>
                </>
            }
            {
                (!isEmpty(props.fhirPreview) && props.fhirPreview.id == null && !props.fhirPreview.loading) &&
                <>
                    <Card>
                        <Tag color="red">Error</Tag>
                        <Tag color="red">Missing Resource ID</Tag>
                        <p>
                            No resource ID found in FHIR preview. A resource ID must be a globally unique string
                            used consistently across all mapping references.
                        </p>
                        <p>
                            For more information: <a href="http://www.hl7.org/fhir/references.html" target="_blank" rel="noopener noreferrer" >
                                FHIR References</a>.
                        </p>
                    </Card>
                    <br/>
                </>
            }
            {(!!props.fhirErrors && props.fhirErrors.issue) &&
            <>
                {props.fhirErrors.issue.map(issue => {
                    let diagnostics = !!issue.diagnostics ? issue.diagnostics : '';
                    let expressions = !!issue.expression ? issue.expression : [];
                    return <Card key={Math.random()} style={{marginBottom: 10}}>
                        <Tag color={getTagColor(issue)}>{issue.severity}</Tag>
                        <Tag color={getTagColor(issue)}>{issue.code}</Tag>
                        <p>{diagnostics}</p>
                        {expressions.map(expression => <Tag key={Math.random()} color={getTagColor(issue)}>{expression}</Tag>)}
                        <br/>
                        <p>{decodeEntities(issue.details.text)}</p>
                    </Card>
                })}
            </>
            }
            {(props.groovyError) &&
            <>
                <Card key={Math.random()} style={{marginBottom: 10}}>
                    <Tag color="orange">Warning</Tag>
                    <Tag color="orange">Groovy Error</Tag>
                    <p>{props.groovyError}</p>
                </Card>
            </>
            }
        </>
    )
}

export default ValidationMessages;
